import type { ObjectDirective } from 'vue';

type InterpolationElement = HTMLElement & {
  $componentUpdated?: () => void;
  $destroy?: () => void;
};

const FILE_PATH_PATTERN = /\/[^/?#]+\.[^/?#]+$/;

const isServiceHref = (href: string) =>
  href.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(href);

const parseHref = (href: string) => {
  try {
    return new URL(href, window.location.href);
  } catch {
    return null;
  }
};

const isFileHref = (href: string) => {
  const url = parseHref(href);

  return url ? FILE_PATH_PATTERN.test(url.pathname) : false;
};

const openInSameTab = (href: string) => {
  window.location.assign(href);
};

const navigate = (event: Event) => {
  const el = event.currentTarget as HTMLElement;
  const href = el.getAttribute('href') || el.getAttribute('data-deeplink');
  if (!href) return;

  if (isServiceHref(href)) return;

  const targetAttr = el.getAttribute('target');

  // только для внутренних ссылок перехватываем

  if (isFileHref(href) || el.hasAttribute('download')) {
    if (el.tagName !== 'A') {
      if (targetAttr === '_blank') {
        window.open(href, '_blank', 'noopener, noreferrer');
        return;
      }

      openInSameTab(href);
    }

    return;
  }

  const url = parseHref(href);
  if (!url) return;

  const isSameOrigin = url.origin === window.location.origin;

  if (isSameOrigin) {
    event.preventDefault();

    return navigateTo(`${url.pathname}${url.search}${url.hash}`, {
      open: targetAttr === '_blank' ? { target: '_blank' } : undefined,
    });
  }

  event.preventDefault();
  window.open(url.href, '_blank', 'noopener, noreferrer');
};

const addListeners = (elements: HTMLElement[]) =>
  elements.forEach((el) => el.addEventListener('click', navigate, false));

const removeListeners = (elements: HTMLElement[]) =>
  elements.forEach((el) => el.removeEventListener('click', navigate, false));

const collectLinks = (root: HTMLElement) =>
  Array.from(root.querySelectorAll('a, span.editor-deep-link')).filter((el) => {
    const href = el.getAttribute('href') || el.getAttribute('data-deeplink');
    return !!href;
  }) as HTMLElement[];

export const vNavigateLinks: ObjectDirective<InterpolationElement> = {
  mounted: (el) => {
    let links = collectLinks(el);
    addListeners(links);

    el.$componentUpdated = () => {
      removeListeners(links);
      nextTick(() => {
        links = collectLinks(el); // ВАЖНО: пересчитать
        addListeners(links);
      });
    };

    el.$destroy = () => removeListeners(links);
  },
  updated: (el) => el.$componentUpdated?.(),
  beforeUnmount: (el) => el.$destroy?.(),
};
